import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ContextInfoData } from "@/api";
import { type StreamStatus, useOperationStream } from "@/streaming/useOperationStream";
import type { AskUserPrompt } from "./AskUserCard";
import { AssistantMessage } from "./AssistantMessage";
import { CapacityWaitNotice } from "./CapacityWaitNotice";
import {
  type ConnectorConnectRequest,
  chatStreamReducer,
  type ExtensionAccessRequest,
  initialChatStream,
} from "./chatStreamReducer";
import { DraftCards } from "./drafts/DraftCards";
import { dedupeSources, numberedSources, parseSources } from "./messageSources";
import { PausedNotice } from "./PausedNotice";
import { ResearchProgress } from "./research/ResearchProgress";
import { ResearchProposalCard } from "./research/ResearchProposalCard";
import { proposalFromEvent, RESEARCH_ERROR_CODES, RESEARCH_TOOL } from "./research/researchModel";
import { StalledNotice } from "./StalledNotice";

const TRANSLATED_ERROR_CODES = ["CONTEXT_TOO_LARGE", "budget_exceeded", ...RESEARCH_ERROR_CODES] as const;
const isTranslatedErrorCode = (code: string | null | undefined): code is (typeof TRANSLATED_ERROR_CODES)[number] =>
  (TRANSLATED_ERROR_CODES as readonly (string | null | undefined)[]).includes(code);

/** The assistant's reply while it streams. Persisted messages take over once it settles. */
export function StreamingAssistantMessage({
  opId,
  name,
  startedAt,
  stopRequested = false,
  retryable = false,
  onSettled,
  onAskUser,
  onExtensionAccess,
  onConnectorConnect,
  onContext,
  onContinue,
  continueDisabled,
}: Readonly<{
  opId: string;
  name?: string | null;
  /** When the reply started (ISO), for a research run's elapsed time. */
  startedAt?: string;
  /** Stop was pressed in the composer. */
  stopRequested?: boolean;
  /** A reply that seems stuck can be stopped and its message sent again. */
  retryable?: boolean;
  /** `retry`: it was stopped to send its message again. */
  onSettled: (status: StreamStatus, retry: boolean) => void;
  /** The model stopped to ask something (answered with a normal message). */
  onAskUser: (prompt: AskUserPrompt) => void;
  /** The reply needed an extension permission the user turned off. */
  onExtensionAccess?: (request: ExtensionAccessRequest) => void;
  /** The reply needed a connector I haven't added my token for. */
  onConnectorConnect?: (request: ConnectorConnectRequest) => void;
  /** Each new context-window report, for the composer's context-usage meter. */
  onContext?: (info: ContextInfoData) => void;
  onContinue?: () => void;
  continueDisabled?: boolean;
}>) {
  const { t } = useTranslation("chat");
  const stoppedToRetry = useRef(false);
  const { state, status, error, stalled, cancel } = useOperationStream(opId, chatStreamReducer, initialChatStream, {
    onSettled: (settled, final) => {
      // A fast stream can settle before React commits the effect below. Preserve its final report.
      if (final.context) onContext?.(final.context);
      if (final.askUser) onAskUser(final.askUser);
      if (final.extensionAccess) onExtensionAccess?.(final.extensionAccess);
      if (final.connectorConnect) onConnectorConnect?.(final.connectorConnect);
      onSettled(settled, stoppedToRetry.current);
    },
  });
  // Not server state: a live per-reply report the composer shows, so it is handed up as it changes.
  useEffect(() => {
    if (state.context) onContext?.(state.context);
  }, [state.context, onContext]);
  // The composer's Stop belongs to the turn; this reply's stream is what stops (and ends it if never confirmed).
  useEffect(() => {
    if (!stopRequested) return;
    stoppedToRetry.current = false; // the latest Stop decides: this one only stops
    void cancel();
  }, [stopRequested, cancel]);
  const stop = (andRetry: boolean) => {
    stoppedToRetry.current = andRetry;
    void cancel();
  };
  // Numbered the way the saved message will be, so a citation points at the same source before and after.
  const citations = useMemo(() => numberedSources(dedupeSources(state.sources)), [state.sources]);
  const streaming = status === "streaming";
  const capacityWait = streaming ? state.capacityWait : null;
  const failure = state.error?.message ?? error?.detail ?? (status === "failed" ? error?.message : null);
  // Translate from the error code when we know it; backend text is English and secondary.
  const code = state.error?.code;
  const failureText = isTranslatedErrorCode(code)
    ? t(`reply.errorCodes.${code}`)
    : failure && t("reply.failed", { message: failure });
  // A research run shows its progress, from its first progress event, instead of a "Using research" badge (the
  // proposal turn starts and ends the same call before its card shows).
  const runs = streaming ? Object.values(state.research) : [];
  const proposal = state.confirmation && proposalFromEvent(state.confirmation);
  const footer = (state.drafts.length > 0 || proposal) && (
    <>
      {proposal && <ResearchProposalCard proposal={proposal} />}
      {state.drafts.length > 0 && <DraftCards drafts={state.drafts} live />}
    </>
  );

  return (
    <AssistantMessage
      name={name}
      meta={streaming ? t("message.responding") : undefined}
      text={state.text}
      sources={parseSources(state.sources)}
      citations={citations}
      tools={state.tools.filter((tool) => tool.name !== RESEARCH_TOOL)}
      streaming={streaming}
      error={failureText || null}
      // Cancelled with no word from the backend: Stop ended it here.
      stopped={state.stopped || status === "cancelled"}
      notice={
        stalled ? (
          <StalledNotice retryable={retryable} onStop={stop} />
        ) : (
          capacityWait && <CapacityWaitNotice wait={capacityWait} />
        )
      }
      activity={
        runs.length > 0 && runs.map((run) => <ResearchProgress key={run.toolCallId} run={run} startedAt={startedAt} />)
      }
      footer={
        <>
          {footer}
          {!streaming && state.pause && (
            <PausedNotice pause={state.pause} onContinue={onContinue} disabled={continueDisabled} />
          )}
        </>
      }
    />
  );
}
